(() => {
  const hasVariantDeepLink = new URLSearchParams(window.location.search).has('variant');
  const selectOptionsLabel = 'Choose options';

  const getProductData = (picker) => {
    if (picker.productData?.variants) return picker.productData;

    const productDataNode = picker
      .closest('.m-main-product--wrapper')
      ?.querySelector('#productData[type="application/json"]');

    try {
      return JSON.parse(productDataNode?.textContent || 'null');
    } catch (_error) {
      return null;
    }
  };

  const getPickerFields = (picker) => Array.from(picker.querySelectorAll('[data-picker-field]'));

  const selectedValues = (picker) =>
    getPickerFields(picker).map((field) => {
      if (field.dataset.pickerField === 'radio') {
        return field.querySelector('input:checked')?.value || '';
      }

      return field.querySelector('select')?.value || '';
    });

  const selectionIsComplete = (picker) => {
    const fields = getPickerFields(picker);
    if (fields.length === 0) return picker.dataset.hasOnlyDefaultVariant === 'true';

    return selectedValues(picker).every(Boolean);
  };

  const getMainVariantInput = (picker) => {
    const form = document.getElementById(`product-form-${picker.dataset.sectionId}`);
    return (
      form?.querySelector('[name="id"]') ||
      picker.closest('.m-main-product--wrapper')?.querySelector('.m-product-form--main [name="id"]') ||
      null
    );
  };

  const getVariantByOptions = (productData, values) =>
    productData?.variants?.find(
      (variant) =>
        variant.options?.length === values.length &&
        values.every((value, index) => variant.options[index] === value)
    );

  const getBestMatchingVariant = (productData, values) => {
    const variants = productData?.variants || [];
    const availableVariants = variants.filter((variant) => variant.available);
    const candidates = availableVariants.length > 0 ? availableVariants : variants;
    const exactVariant = getVariantByOptions(productData, values);

    if (exactVariant?.available || (exactVariant && availableVariants.length === 0)) {
      return exactVariant;
    }

    let prefixLength = values.findIndex((value) => !value);
    if (prefixLength < 0) prefixLength = values.length;

    for (let length = prefixLength; length > 0; length -= 1) {
      const match = candidates.find((variant) =>
        values.slice(0, length).every((value, index) => variant.options[index] === value)
      );
      if (match) return match;
    }

    const selectedPositions = values
      .map((value, index) => ({ value, index }))
      .filter(({ value }) => Boolean(value));

    for (const { value, index } of selectedPositions) {
      const match = candidates.find((variant) => variant.options[index] === value);
      if (match) return match;
    }

    return candidates[0];
  };

  const setFieldValue = (field, value) => {
    if (field.dataset.pickerField === 'radio') {
      const inputs = Array.from(field.querySelectorAll('input[type="radio"]'));
      inputs.forEach((input) => {
        input.checked = input.value === value;
      });
    } else {
      const select = field.querySelector('select');
      if (select) select.value = value;
    }

    field.value = value;
    field.dataset.selectedValue = value;
    const selectedLabel = field.querySelector('.option-label--selected');
    if (selectedLabel) selectedLabel.textContent = value;
  };

  const applyVariantSelection = (picker, variant) => {
    if (!variant?.options) return;

    getPickerFields(picker).forEach((field, index) => {
      const value = variant.options[index];
      if (value != null) setFieldValue(field, value);
    });
  };

  const updateDependentOptions = (picker, productData) => {
    const values = selectedValues(picker);
    const availableVariants = (productData?.variants || []).filter((variant) => variant.available);
    const variants = availableVariants.length > 0 ? availableVariants : productData?.variants || [];

    getPickerFields(picker).forEach((field, optionIndex) => {
      const matchingVariants = variants.filter((variant) =>
        values
          .slice(0, optionIndex)
          .every((selectedValue, index) => !selectedValue || variant.options[index] === selectedValue)
      );

      field.querySelectorAll('.m-product-option--node').forEach((node) => {
        const value = node.dataset.value;
        const isAvailable = matchingVariants.some((variant) => variant.options[optionIndex] === value);
        const input = node.matches('option') ? node : node.querySelector('input');

        node.classList.toggle('m-product-option--node__unavailable', !isAvailable);
        if (input) input.disabled = !isAvailable;
      });
    });
  };

  const getInitialVariant = (picker, productData) => {
    const variants = productData?.variants || [];
    const queryVariantId = Number(new URLSearchParams(window.location.search).get('variant'));
    const inputVariantId = Number(getMainVariantInput(picker)?.value);
    const productVariantId = Number(productData?.selected_or_first_available_variant?.id);

    return (
      variants.find((variant) => variant.id === queryVariantId) ||
      variants.find((variant) => variant.id === inputVariantId) ||
      variants.find((variant) => variant.id === productVariantId) ||
      variants.find((variant) => variant.available) ||
      variants[0]
    );
  };

  const requiresManualSelection = (picker, productData) =>
    !hasVariantDeepLink &&
    picker.dataset.disableSelectedVariantDefault === 'true' &&
    (productData?.variants?.length || 0) > 1;

  const setCheckoutState = (picker, enabled) => {
    const wrapper = picker.closest('.m-main-product--wrapper');
    wrapper?.querySelectorAll('.m-product-dynamic-checkout').forEach((checkout) => {
      checkout.classList.toggle('disabled', !enabled);
      checkout.setAttribute('aria-disabled', String(!enabled));
    });
  };

  const setUnavailable = (picker, clearVariantInput = false) => {
    const input = getMainVariantInput(picker);
    if (input && clearVariantInput) input.value = '';

    picker.currentVariant = undefined;
    picker.options = selectedValues(picker).filter(Boolean);
    picker.updateButton?.(true, selectOptionsLabel);
    setCheckoutState(picker, false);
  };

  const updatePickerForVariant = (picker, variant, updateUrl = false) => {
    if (!variant) return false;

    const input = getMainVariantInput(picker);
    applyVariantSelection(picker, variant);
    picker.currentVariant = variant;
    picker.options = [...variant.options];
    picker.productData.current_variant_id = variant.id;
    picker.productData.initialVariant ||= variant;

    if (input) {
      input.value = String(variant.id);
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    picker.updateVariantInput?.();
    picker.updateProductMeta?.();
    picker.updatePrice?.();
    picker.updateButton?.(!variant.available, variant.available ? '' : window.MinimogStrings?.soldOut);
    picker.hideSoldOutAndUnavailableOptions?.();
    updateDependentOptions(picker, picker.productData);
    picker.updatePickupAvailability?.();

    if (picker.media) {
      picker.getDataImageVariant?.(variant.id);
      picker.updateMedia?.();
    }

    if (updateUrl) picker.updateBrowserHistory?.();

    setCheckoutState(picker, variant.available);
    window.MinimogEvents?.emit?.(`${picker.dataset.productId}__VARIANT_CHANGE`, variant, picker);
    return true;
  };

  const syncPicker = (picker, updateUrl = false) => {
    const productData = getProductData(picker);
    if (!productData?.variants?.length) return false;

    picker.productData = productData;
    const values = selectedValues(picker);
    const fields = getPickerFields(picker);
    const hasSelection = values.some(Boolean);
    let variant = null;

    if (fields.length === 0) {
      variant = getInitialVariant(picker, productData);
    } else if (hasSelection) {
      variant = getBestMatchingVariant(productData, values);
    } else if (!requiresManualSelection(picker, productData)) {
      variant = getInitialVariant(picker, productData);
    }

    if (!variant) {
      updateDependentOptions(picker, productData);
      setUnavailable(picker, true);
      return false;
    }

    return updatePickerForVariant(picker, variant, updateUrl);
  };

  const resetInitialSelection = (picker) => {
    const productData = getProductData(picker);
    if (!requiresManualSelection(picker, productData)) return;

    picker.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.checked = false;
    });
    picker.querySelectorAll('[data-picker-field="radio"]').forEach((field) => {
      field.dataset.selectedValue = '';
    });
    picker.querySelectorAll('[data-picker-field="select"]').forEach((field) => {
      const select = field.querySelector('select');
      if (select) select.value = '';
      field.dataset.selectedValue = '';
    });
    picker.querySelectorAll('.option-label--selected').forEach((label) => {
      label.textContent = '';
    });

    setUnavailable(picker, true);

    document
      .querySelectorAll('sticky-atc[data-disable-selected-variant-default="true"] select[name="id"]')
      .forEach((select) => {
        select.value = '';
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
  };

  customElements.whenDefined('variant-picker').then(() => {
    const VariantPicker = customElements.get('variant-picker');
    const prototype = VariantPicker?.prototype;

    if (prototype && !prototype.tesevoVariantSyncPatched) {
      const originalSelectInitialVariant = prototype.selectInitialVariant;
      const originalSetProductAsUnavailable = prototype.setProductAsUnavailable;

      prototype.selectInitialVariant = function selectScopedInitialVariant() {
        originalSelectInitialVariant.call(this);

        if (!this.currentVariant && !this.selectedVariantDefault) {
          const productData = getProductData(this);
          const variant = getInitialVariant(this, productData);
          if (variant) updatePickerForVariant(this, variant);
        }
      };

      prototype.getSelectedVariant = function getExactSelectedVariant() {
        const productData = getProductData(this);
        const values = selectedValues(this);
        this.options = values.filter(Boolean);
        const fields = getPickerFields(this);
        const hasSelection = values.some(Boolean);

        if (fields.length === 0) {
          this.currentVariant = getInitialVariant(this, productData);
        } else if (hasSelection) {
          this.currentVariant = getBestMatchingVariant(productData, values);
        } else {
          this.currentVariant = requiresManualSelection(this, productData)
            ? undefined
            : getInitialVariant(this, productData);
        }

        if (this.currentVariant) {
          applyVariantSelection(this, this.currentVariant);
          this.options = [...this.currentVariant.options];
        }
      };

      prototype.setProductAsUnavailable = function setUnavailableAfterManualChoice() {
        if (!selectionIsComplete(this)) {
          setUnavailable(this, true);
          return;
        }

        originalSetProductAsUnavailable.call(this);
      };

      prototype.tesevoVariantSyncPatched = true;
    }

    document.querySelectorAll('variant-picker').forEach((picker) => {
      resetInitialSelection(picker);
      syncPicker(picker);

      if (picker.dataset.tesevoVariantSyncBound === 'true') return;
      picker.dataset.tesevoVariantSyncBound = 'true';
      picker.addEventListener('change', () => {
        window.requestAnimationFrame(() => syncPicker(picker, true));
      });
    });
  });
})();
